import React, { useEffect, useRef, useState } from "react";
import { Button, Spinner, Form } from "react-bootstrap";
import Webcam from "react-webcam";
import API from "../services/api";
import "./AttendanceForm.css";

const reverseGeocode = async (lat, lng) => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
        lat
      )}&lon=${encodeURIComponent(lng)}`
    );
    if (!res.ok) return "";
    const data = await res.json();
    return data?.display_name || "";
  } catch {
    return "";
  }
};

const GuardAttendance = () => {
  const [guard, setGuard] = useState(null);
  const [record, setRecord] = useState(null);
  const [location, setLocation] = useState({ lat: "", lng: "" });
  const [locationAddress, setLocationAddress] = useState("");
  const [selfie, setSelfie] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [message, setMessage] = useState("");
  const webcamRef = useRef(null);

  const fetchGuard = async () => {
    try {
      const res = await API.get("/auth/me");
      setGuard(res.data);
    } catch (err) {
      console.error("Failed to load guard info:", err);
    }
  };

  const fetchToday = async () => {
    try {
      const res = await API.get("/guard/attendance/today");
      setRecord(res.data);
    } catch (err) {
      console.error("Failed to fetch attendance:", err);
    }
  };

  const fetchLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setLocation(coords);
        const readable = await reverseGeocode(coords.lat, coords.lng);
        if (readable) setLocationAddress(readable);
      },
      () => {
        setMessage("Location access denied. Please enable GPS.");
      }
    );
  };

  useEffect(() => {
    fetchGuard();
    fetchToday();
    fetchLocation();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const handle = (e) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);
    if (mq.addEventListener) {
      mq.addEventListener("change", handle);
      return () => mq.removeEventListener("change", handle);
    }
    mq.addListener(handle);
    return () => mq.removeListener(handle);
  }, []);

  const captureSelfie = () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) setSelfie(imageSrc);
  };

  const handlePunchIn = async () => {
    setMessage("");
    if (!selfie) {
      setMessage("Please capture a selfie for punch-in.");
      return;
    }
    setLoading(true);
    try {
      const res = await API.post("/guard/attendance/punch-in", {
        location,
        address: locationAddress,
        selfie,
      });
      setMessage(res.data?.message || "Punch-in recorded.");
      if (res.data?.record) {
        setRecord(res.data.record);
      } else {
        fetchToday();
      }
    } catch (err) {
      setMessage(err.response?.data?.message || "Punch-in failed.");
    } finally {
      setLoading(false);
    }
  };

  const handlePunchOut = async () => {
    setMessage("");
    setLoading(true);
    try {
      const res = await API.post("/guard/attendance/punch-out", { location, address: locationAddress });
      setMessage(res.data?.message || "Punch-out recorded.");
      if (res.data?.record) {
        setRecord(res.data.record);
      } else {
        fetchToday();
      }
    } catch (err) {
      setMessage(err.response?.data?.message || "Punch-out failed.");
    } finally {
      setLoading(false);
    }
  };

  const formatStatus = (status) => {
    if (!status) return "Not marked";
    const s = String(status).toLowerCase();
    if (s === "halfday" || s === "half-day") return "Half Day";
    if (s === "present") return "Present";
    if (s === "absent") return "Absent";
    if (s === "in") return "In";
    if (s === "completed") return "Completed";
    return status;
  };

  return (
    <div className="attendance-shell">
      <div className="attendance-left">
        <div className="attendance-header">
          <button type="button" className="back-btn" onClick={() => window.history.back()}>
            <span className="material-symbols-outlined">arrow_back_ios</span>
          </button>
          <h2>Guard Attendance</h2>
        </div>

        <div className="attendance-hero">
          <div className="hero-ring">
            <div className="hero-core">
              <span className="material-symbols-outlined">shield</span>
            </div>
          </div>
          <h1>Daily Punch</h1>
          <p>Capture selfie and mark punch in/out</p>
        </div>

        <div className="attendance-card">
          {message && (
            <div className={`attendance-message ${message.includes("✅") ? "success" : "error"}`}>
              {message}
            </div>
          )}

          <Form>
            <div className="detail-group">
              <p>Guard</p>
              <h3>{guard?.fullName || "Guard"}</h3>
            </div>

            <div className="detail-grid">
              <div>
                <p>Status</p>
                <div className="detail-value">{formatStatus(record?.status)}</div>
              </div>
              <div>
                <p>Mobile</p>
                <div className="detail-value">{guard?.mobile || "-"}</div>
              </div>
            </div>

            <div className="detail-row">
              <p>Location</p>
              <div className="detail-value">
                {locationAddress || (location.lat && location.lng ? "Location captured" : "Location not detected")}
              </div>
            </div>

            <div className="detail-row">
              <p>Punch In</p>
              <div className="detail-value">
                {record?.punchIn?.time ? new Date(record.punchIn.time).toLocaleString() : "--"}
              </div>
            </div>
            <div className="detail-row">
              <p>Punch Out</p>
              <div className="detail-value">
                {record?.punchOut?.time ? new Date(record.punchOut.time).toLocaleString() : "--"}
              </div>
            </div>

            {!isDesktop && (
              <div className="selfie-block">
                <p>Live Selfie</p>
                {selfie ? (
                  <>
                    <img src={selfie} alt="Captured" />
                    <Button variant="warning" onClick={() => setSelfie(null)}>
                      Retake
                    </Button>
                  </>
                ) : (
                  <>
                    <Webcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      width="100%"
                      videoConstraints={{ facingMode: "user" }}
                    />
                    <Button variant="primary" onClick={captureSelfie}>
                      Capture Selfie
                    </Button>
                  </>
                )}
              </div>
            )}

            <div className="actions">
              <Button variant="success" type="button" disabled={loading} onClick={handlePunchIn}>
                {loading ? <Spinner size="sm" animation="border" /> : "Punch In"}
              </Button>
              <Button variant="secondary" type="button" disabled={loading} onClick={handlePunchOut}>
                {loading ? <Spinner size="sm" animation="border" /> : "Punch Out"}
              </Button>
            </div>
          </Form>
        </div>
      </div>

      {isDesktop && (
        <div className="attendance-right">
          <div className="attendance-photo-card">
            <div className="photo-title">Live Selfie</div>
            {selfie ? (
              <>
                <img src={selfie} alt="Captured" />
                <Button variant="warning" onClick={() => setSelfie(null)}>
                  Retake
                </Button>
              </>
            ) : (
              <>
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  width="100%"
                  videoConstraints={{ facingMode: "user" }}
                />
                <Button variant="primary" onClick={captureSelfie}>
                  Capture Selfie
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GuardAttendance;
